import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  confirmCaptureBlock,
  listCaptureSession,
  proposeCaptureSession,
} from "../../components/funtionsBD/Mainservice";
import {
  localizedDraftName,
  localizedExerciseName,
  localizedTypeName,
} from "../../utils/localizedCatalogFields";
import "./LiveTrainingCaptureSummary.css";

function typeLabel(block, lang) {
  return localizedTypeName(block?.type_ex, lang) || "";
}

function playerNotes(block) {
  return (block?.segments || []).filter((row) => row.kind === "player_note");
}

export function LiveTrainingCaptureSummary({
  open,
  tsId,
  lang = "en",
  onClose,
  onConfirmed,
}) {
  const { t } = useTranslation();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyTeId, setBusyTeId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!tsId) return;
    setLoading(true);
    setError("");
    try {
      await proposeCaptureSession({ ts_id: tsId, lang });
      const response = await listCaptureSession(tsId);
      setBlocks(response?.data?.data?.blocks || []);
    } catch (err) {
      setError(err?.message || t("general", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  }, [tsId, lang, t]);

  useEffect(() => {
    if (!open) return undefined;
    void load();
    return undefined;
  }, [open, load]);

  const confirm = async (teId, choice) => {
    setBusyTeId(teId);
    setError("");
    try {
      await confirmCaptureBlock({ te_id: teId, choice });
      await load();
      onConfirmed?.();
    } catch (err) {
      setError(err?.message || t("general", "Something went wrong"));
    } finally {
      setBusyTeId(null);
    }
  };

  if (!open) return null;

  const pending = blocks.filter((block) => block.status !== "confirmed" || !block.ex_id);

  return (
    <div className="lt-capture-summary-backdrop" role="presentation" onClick={onClose}>
      <div
        className="lt-capture-summary"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-capture-summary-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="lt-capture-summary__header">
          <div>
            <h2 id="lt-capture-summary-title">
              {t("liveTrainingCaptureSummaryTitle", "Fill the session")}
            </h2>
            <p>
              {t(
                "liveTrainingCaptureSummaryHint",
                "Pingflo matched each block from what you said. Confirm a catalog exercise or save a new one."
              )}
            </p>
          </div>
          <button type="button" className="lt-capture-summary__close" onClick={onClose}>
            {t("close", "Close")}
          </button>
        </header>

        {error ? (
          <p className="lt-capture-summary__error" role="alert">
            {error}
          </p>
        ) : null}
        {loading ? <p>{t("loading", "Loading…")}</p> : null}

        <div className="lt-capture-summary__list">
          {blocks.map((block) => {
            const proposal = block.proposal || {};
            const notes = playerNotes(block);
            const confirmed = block.status === "confirmed" && block.ex_id;
            const draftLabel =
              localizedDraftName(proposal, lang) ||
              localizedDraftName(proposal.draft, lang);
            const matchLabel =
              localizedExerciseName(proposal.match, lang) ||
              proposal.match?.name ||
              "";
            return (
              <article key={block.te_id} className="lt-capture-summary__card">
                <h3>
                  {block.te_order != null ? `${block.te_order}. ` : ""}
                  {typeLabel(block, lang) || t("exercise", "Exercise")}
                </h3>
                {proposal.explanation ? (
                  <p className="lt-capture-summary__speech">{proposal.explanation}</p>
                ) : (
                  <p className="lt-capture-summary__speech">
                    {t(
                      "liveTrainingCaptureNoSpeech",
                      "No explanation was captured for this block."
                    )}
                  </p>
                )}
                {notes.length ? (
                  <ul className="lt-capture-summary__notes">
                    {notes.map((note) => (
                      <li key={note.segment_id}>{note.transcript}</li>
                    ))}
                  </ul>
                ) : null}
                {confirmed ? (
                  <p className="lt-capture-summary__done">
                    {t("liveTrainingCaptureConfirmed", "Exercise details saved")}
                  </p>
                ) : (
                  <div className="lt-capture-summary__actions">
                    {proposal.match?.ex_id ? (
                      <button
                        type="button"
                        disabled={busyTeId === block.te_id}
                        onClick={() => confirm(block.te_id, "match")}
                      >
                        {t("liveTrainingCaptureUseMatch", "Use catalog")}
                        {matchLabel ? `: ${matchLabel}` : ""}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={busyTeId === block.te_id}
                      onClick={() => confirm(block.te_id, "create")}
                    >
                      {t("liveTrainingCaptureCreate", "Save as new exercise")}
                      {draftLabel ? `: ${draftLabel}` : ""}
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {!loading && pending.length === 0 && blocks.length > 0 ? (
          <p className="lt-capture-summary__done">
            {t("liveTrainingCaptureAllDone", "Every block has an exercise.")}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default LiveTrainingCaptureSummary;
